import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box, Typography, TextField, Button, IconButton, Chip,
  MenuItem, Select, FormControl, InputLabel,
  Tooltip, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { db, storage } from '../../../../firebase/config';
import {
  doc, setDoc, deleteDoc, collection, Timestamp, writeBatch,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { toast } from 'sonner';
import {
  uid, STATUS_OPTIONS, IMPL_OPTIONS, TECH_TYPES, REF_TYPES, MAPPN_ACCENT,
} from '../constants';
import CommentThread from './CommentThread';

// ── Helpers ─────────────────────────────────────────────────────────────────

// Recursively strip null/undefined values and empty-string keys so the
// Firestore SDK never tries to build a field mask against a null value.
function deepStrip(val) {
  if (Array.isArray(val)) return val.map(deepStrip);
  if (val !== null && val !== undefined && typeof val === 'object'
      && typeof val.toDate !== 'function' && typeof val.toMillis !== 'function') {
    const out = {};
    for (const [k, v] of Object.entries(val)) {
      if (k === '' || k == null || v === null || v === undefined) continue;
      out[k] = deepStrip(v);
    }
    return out;
  }
  return val;
}

function SectionLabel({ children }) {
  return (
    <Typography variant="caption" fontWeight={700} sx={{
      textTransform: 'uppercase', letterSpacing: 1,
      color: 'rgba(255,255,255,0.35)', display: 'block', mb: 1,
    }}>
      {children}
    </Typography>
  );
}

const DF = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' },
  '& .MuiOutlinedInput-root': {
    color: 'white', fontSize: '0.85rem',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: MAPPN_ACCENT },
  },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
};

// ── Sortable feature row ─────────────────────────────────────────────────────

function FeatureRow({ feat, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: feat.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <Box ref={setNodeRef} style={style} sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0.5,
      bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, p: 1, mb: 0.5,
    }}>
      <Box {...attributes} {...listeners} sx={{ color: 'rgba(255,255,255,0.2)', cursor: 'grab', mt: 0.5 }}>
        <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <TextField
          placeholder="Funktionsnamn"
          value={feat.name}
          onChange={e => onChange({ ...feat, name: e.target.value })}
          size="small" fullWidth sx={DF}
          inputProps={{ style: { fontSize: '0.82rem', fontWeight: 600 } }}
        />
        <TextField
          placeholder="Beskrivning (valfri)"
          value={feat.description}
          onChange={e => onChange({ ...feat, description: e.target.value })}
          size="small" fullWidth multiline maxRows={3} sx={DF}
          inputProps={{ style: { fontSize: '0.78rem' } }}
        />
      </Box>
      <IconButton size="small" onClick={() => onDelete(feat.id)}
        sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' } }}>
        <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
      </IconButton>
    </Box>
  );
}

// ── Tech stack row ────────────────────────────────────────────────────────────

function TechRow({ tech, onChange, onDelete }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      <Select
        value={tech.type}
        onChange={e => onChange({ ...tech, type: e.target.value })}
        size="small" sx={{ ...DF, width: 120, '& .MuiSelect-select': { py: 0.6, fontSize: '0.75rem' } }}
      >
        {TECH_TYPES.map(t => (
          <MenuItem key={t.key} value={t.key} sx={{ fontSize: '0.8rem' }}>
            <Chip label={t.label} size="small"
              sx={{ bgcolor: t.bg, color: t.color, fontSize: '0.65rem', height: 18, mr: 0.5 }} />
          </MenuItem>
        ))}
      </Select>
      <TextField
        placeholder="Namn *"
        value={tech.name}
        onChange={e => onChange({ ...tech, name: e.target.value })}
        size="small" sx={{ ...DF, flex: 2 }}
        inputProps={{ style: { fontSize: '0.8rem' } }}
      />
      <TextField
        placeholder="Version"
        value={tech.version}
        onChange={e => onChange({ ...tech, version: e.target.value })}
        size="small" sx={{ ...DF, width: 90 }}
        inputProps={{ style: { fontSize: '0.78rem' } }}
      />
      <TextField
        placeholder="URL"
        value={tech.url}
        onChange={e => onChange({ ...tech, url: e.target.value })}
        size="small" sx={{ ...DF, flex: 3 }}
        inputProps={{ style: { fontSize: '0.78rem' } }}
      />
      <IconButton size="small" onClick={() => onDelete(tech.id)}
        sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' } }}>
        <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
      </IconButton>
    </Box>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function ComponentDetail({
  open, caseId, componentId, initialData,
  perspectives, categories, caseTechStack,
  onClose, onSaved, onDeleted,
}) {
  const isNew = componentId === '__new__';
  const [comp, setComp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const avatarInputRef = useRef(null);
  const saveTimer = useRef(null);
  const savingRef = useRef(false);
  const newDocRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Init comp when panel opens or target changes
  useEffect(() => {
    if (!open) {
      newDocRef.current = null;
      return;
    }
    if (isNew) {
      newDocRef.current = null;
      setComp(blankComp());
    } else if (initialData) {
      setComp({ ...blankComp(), ...initialData });
    }
  }, [open, componentId]); // eslint-disable-line

  function blankComp() {
    return {
      title: '',
      description: '',
      avatarUrl: '',
      categoryId: '',
      perspectiveIds: [],
      status: 'ide',
      implementation: 'native',
      externalSystems: [],
      features: [],
      techStack: [],
      references: [],
      notes: '',
      priorityByPerspective: {},
    };
  }

  // Always keep a ref to the latest doSave so the auto-save timer
  // never closes over a stale componentId from a previous render.
  const doSaveRef = useRef(null);
  // (assigned below after doSave is defined)

  // Clear pending auto-save when switching components or closing
  useEffect(() => {
    return () => clearTimeout(saveTimer.current);
  }, [componentId]);

  // Debounced auto-save — only for existing components
  const autoSave = useCallback((data) => {
    if (isNew) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSaveRef.current?.(data, true), 1500);
  }, [isNew]);

  function update(patch) {
    setComp(prev => {
      const next = { ...prev, ...patch };
      autoSave(next);
      return next;
    });
  }

  async function doSave(data, silent = false) {
    if (!data?.title?.trim()) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      let ref;
      if (isNew) {
        if (!newDocRef.current) {
          newDocRef.current = doc(collection(db, 'mappnCases', caseId, 'components'));
        }
        ref = newDocRef.current;
      } else {
        ref = doc(db, 'mappnCases', caseId, 'components', componentId);
      }
      // Sanitize every array/object field before writing to Firestore.
      // position is deliberately excluded — it's managed separately by onNodeDragStop.
      const sanitizedFeatures = (data.features || []).map(f => ({
        id: f.id ?? '', name: f.name ?? '', description: f.description ?? '',
        order: f.order ?? 0,
      }));
      const sanitizedTechStack = (data.techStack || []).map(t => ({
        id: t.id ?? '', name: t.name ?? '', type: t.type ?? 'package',
        version: t.version ?? '', url: t.url ?? '', note: t.note ?? '',
      }));
      const sanitizedExternalSystems = (data.externalSystems || []).map(s => ({
        id: s.id ?? '', name: s.name ?? '', url: s.url ?? '', description: s.description ?? '',
      }));
      const sanitizedReferences = (data.references || []).map(r => ({
        id: r.id ?? '', name: r.name ?? '', url: r.url ?? '',
        type: r.type ?? 'inspiration', note: r.note ?? '',
      }));
      const payload = {
        title:                 data.title ?? '',
        description:           data.description ?? '',
        avatarUrl:             data.avatarUrl ?? '',
        categoryId:            data.categoryId ?? '',
        perspectiveIds:        (data.perspectiveIds || []).filter(id => id != null),
        status:                data.status ?? 'ide',
        implementation:        data.implementation ?? 'native',
        externalSystems:       sanitizedExternalSystems,
        features:              sanitizedFeatures,
        techStack:             sanitizedTechStack,
        references:            sanitizedReferences,
        notes:                 data.notes ?? '',
        priorityByPerspective: Object.fromEntries(
          Object.entries(data.priorityByPerspective || {}).filter(([k, v]) => k && v != null)
        ),
        updatedAt: Timestamp.now(),
      };
      const now = Timestamp.now();
      const cleanPos = {};
      if (data.position && typeof data.position === 'object') {
        for (const [k, v] of Object.entries(data.position)) {
          if (k && v !== null && v !== undefined && typeof v === 'object') cleanPos[k] = v;
        }
      }
      const base = isNew
        ? { ...payload, position: {}, createdAt: now }
        : { ...payload, position: cleanPos, createdAt: data.createdAt instanceof Timestamp ? data.createdAt : now };
      const clean = deepStrip(base);
      if (isNew) {
        await setDoc(ref, clean);
      } else {
        // Atomically delete then recreate — clears any cached null-valued fields
        // that would otherwise crash Firebase SDK's internal diff (null.indexOf bug).
        const batch = writeBatch(db);
        batch.delete(ref);
        batch.set(ref, clean);
        await batch.commit();
      }
      if (!silent) toast.success('Sparad', { duration: 1500 });
      onSaved?.(ref.id, data);
      savingRef.current = false;
      setSaving(false);
      return true;
    } catch (e) {
      console.error(e);
      toast.error('Kunde inte spara: ' + (e?.code || e?.message || String(e)));
    }
    savingRef.current = false;
    setSaving(false);
    return false;
  }
  doSaveRef.current = doSave;

  async function handleDelete() {
    if (!window.confirm(`Ta bort "${comp?.title || 'komponenten'}"?`)) return;
    try {
      await deleteDoc(doc(db, 'mappnCases', caseId, 'components', componentId));
      toast.success('Komponent raderad', { duration: 1500 });
      onDeleted?.(componentId);
    } catch (e) {
      toast.error('Kunde inte radera');
    }
  }

  async function uploadAvatar(file) {
    if (!file) return;
    const docId = isNew
      ? (newDocRef.current || (newDocRef.current = doc(collection(db, 'mappnCases', caseId, 'components')))).id
      : componentId;
    const path = `mappn/${caseId}/${docId}/avatar_${Date.now()}`;
    const sRef = storageRef(storage, path);
    setUploadProgress(0);
    const task = uploadBytesResumable(sRef, file);
    task.on('state_changed',
      snap => setUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      err  => { console.error(err); toast.error('Uppladdning misslyckades'); setUploadProgress(null); },
      async () => {
        const url = await getDownloadURL(sRef);
        setUploadProgress(null);
        update({ avatarUrl: url });
        // delete old avatar if any
        if (comp?.avatarUrl) {
          try { await deleteObject(storageRef(storage, comp.avatarUrl)); } catch (_) {}
        }
      },
    );
  }

  async function removeAvatar() {
    if (comp?.avatarUrl) {
      try { await deleteObject(storageRef(storage, comp.avatarUrl)); } catch (_) {}
    }
    update({ avatarUrl: '' });
  }

  // Feature DnD
  function onFeatureDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    setComp(prev => {
      const feats = prev.features;
      const from = feats.findIndex(f => f.id === active.id);
      const to   = feats.findIndex(f => f.id === over.id);
      const next = { ...prev, features: arrayMove(feats, from, to) };
      autoSave(next);
      return next;
    });
  }

  if (!comp && !isNew) return null;
  const data = comp || blankComp();

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0, zIndex: 1200,
              background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)',
            }}
          />

          {/* Panel */}
          <motion.div
            key="panel"
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 340, damping: 36 }}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: 640, zIndex: 1300,
              background: '#161b22',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Panel header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', px: 2.5, py: 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              gap: 1, flexShrink: 0,
            }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, color: 'white' }}>
                {isNew ? 'Ny komponent' : (data.title || 'Komponent')}
              </Typography>
              {saving && <CircularProgress size={14} sx={{ color: MAPPN_ACCENT }} />}
              {!isNew && (
                <Tooltip title="Ta bort komponent">
                  <IconButton size="small" onClick={handleDelete}
                    sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#EF5350' } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* ── Grunddata ─────────────────────────────────────────────── */}
              <Section>
                <SectionLabel>Grunddata</SectionLabel>
                <Box sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'flex-start' }}>
                  {/* Avatar */}
                  <Box sx={{ flexShrink: 0 }}>
                    <Tooltip title={data.avatarUrl ? 'Byt bild' : 'Ladda upp logotyp/bild'}>
                      <Box
                        onClick={() => avatarInputRef.current?.click()}
                        sx={{
                          width: 56, height: 56, borderRadius: 2,
                          border: `2px dashed ${data.avatarUrl ? 'transparent' : 'rgba(255,255,255,0.18)'}`,
                          bgcolor: 'rgba(255,255,255,0.04)',
                          cursor: 'pointer', overflow: 'hidden',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          position: 'relative',
                          '&:hover': { borderColor: MAPPN_ACCENT + '88', bgcolor: MAPPN_ACCENT + '0d' },
                          transition: 'all 0.15s',
                        }}
                      >
                        {uploadProgress !== null ? (
                          <CircularProgress size={22} sx={{ color: MAPPN_ACCENT }} variant="determinate" value={uploadProgress} />
                        ) : data.avatarUrl ? (
                          <Box component="img" src={data.avatarUrl}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', lineHeight: 1.3, px: 0.5 }}>
                            Bild
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                    {data.avatarUrl && (
                      <Typography
                        onClick={removeAvatar}
                        sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.25)', textAlign: 'center', cursor: 'pointer', mt: 0.5,
                          '&:hover': { color: '#EF5350' } }}
                      >
                        Ta bort
                      </Typography>
                    )}
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={e => { uploadAvatar(e.target.files[0]); e.target.value = ''; }}
                    />
                  </Box>

                  {/* Title + description */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      label="Titel *"
                      value={data.title}
                      onChange={e => update({ title: e.target.value })}
                      fullWidth size="small" sx={DF}
                    />
                    <TextField
                      label="Beskrivning"
                      value={data.description}
                      onChange={e => update({ description: e.target.value })}
                      fullWidth size="small" multiline rows={3} sx={DF}
                    />
                  </Box>
                </Box>
              </Section>

              {/* ── Klassificering ────────────────────────────────────────── */}
              <Section>
                <SectionLabel>Klassificering</SectionLabel>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <FormControl size="small" sx={{ flex: 1, ...DF }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={data.status} onChange={e => update({ status: e.target.value })}>
                      {STATUS_OPTIONS.map(s => (
                        <MenuItem key={s.key} value={s.key} sx={{ fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                            {s.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ flex: 1, ...DF }}>
                    <InputLabel>Kategori</InputLabel>
                    <Select label="Kategori" value={data.categoryId} onChange={e => update({ categoryId: e.target.value })}>
                      <MenuItem value=""><em>Ingen</em></MenuItem>
                      {categories.map(c => (
                        <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, flexShrink: 0 }} />
                            {c.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Perspektiv multiselect */}
                <SectionLabel>Perspektiv</SectionLabel>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {perspectives.map(p => {
                    const active = data.perspectiveIds.includes(p.id);
                    return (
                      <Chip
                        key={p.id}
                        label={p.name}
                        size="small"
                        onClick={() => update({
                          perspectiveIds: active
                            ? data.perspectiveIds.filter(x => x !== p.id)
                            : [...data.perspectiveIds, p.id],
                        })}
                        sx={{
                          bgcolor: active ? p.color + '33' : 'rgba(255,255,255,0.04)',
                          color: active ? p.color : 'rgba(255,255,255,0.4)',
                          border: `1px solid ${active ? p.color + '66' : 'transparent'}`,
                          cursor: 'pointer', fontWeight: active ? 700 : 400,
                          '&:hover': { bgcolor: p.color + '22' },
                        }}
                      />
                    );
                  })}
                  {perspectives.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                      Skapa perspektiv i Inställningar
                    </Typography>
                  )}
                </Box>
              </Section>

              {/* ── Implementation ────────────────────────────────────────── */}
              <Section>
                <SectionLabel>Implementation</SectionLabel>
                <Box sx={{ display: 'flex', gap: 1, mb: data.implementation !== 'native' ? 1.5 : 0 }}>
                  {IMPL_OPTIONS.map(opt => (
                    <Box
                      key={opt.key}
                      onClick={() => update({ implementation: opt.key })}
                      sx={{
                        px: 1.5, py: 0.75, borderRadius: 1, cursor: 'pointer',
                        border: `1px solid ${data.implementation === opt.key ? opt.color + '88' : 'rgba(255,255,255,0.1)'}`,
                        bgcolor: data.implementation === opt.key ? opt.color + '22' : 'transparent',
                        color: data.implementation === opt.key ? opt.color : 'rgba(255,255,255,0.4)',
                        fontSize: '0.8rem', fontWeight: data.implementation === opt.key ? 700 : 400,
                        transition: 'all 0.15s',
                      }}
                    >
                      {opt.label}
                    </Box>
                  ))}
                </Box>

                {data.implementation !== 'native' && (
                  <>
                    <SectionLabel>Externa system</SectionLabel>
                    {data.externalSystems.map((sys, i) => (
                      <Box key={sys.id} sx={{
                        display: 'flex', gap: 1, mb: 0.75,
                        bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, p: 1,
                      }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          <TextField
                            placeholder="Systemnamn *"
                            value={sys.name}
                            onChange={e => {
                              const updated = data.externalSystems.map((s, j) => j === i ? { ...s, name: e.target.value } : s);
                              update({ externalSystems: updated });
                            }}
                            size="small" fullWidth sx={DF}
                            inputProps={{ style: { fontSize: '0.82rem' } }}
                          />
                          <Box sx={{ display: 'flex', gap: 0.75 }}>
                            <TextField
                              placeholder="URL"
                              value={sys.url}
                              onChange={e => {
                                const updated = data.externalSystems.map((s, j) => j === i ? { ...s, url: e.target.value } : s);
                                update({ externalSystems: updated });
                              }}
                              size="small" sx={{ ...DF, flex: 1 }}
                              inputProps={{ style: { fontSize: '0.78rem' } }}
                            />
                            {sys.url && (
                              <IconButton size="small" href={sys.url} target="_blank" rel="noopener"
                                sx={{ color: MAPPN_ACCENT }}>
                                <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            )}
                          </Box>
                          <TextField
                            placeholder="Vad bidrar systemet med?"
                            value={sys.description}
                            onChange={e => {
                              const updated = data.externalSystems.map((s, j) => j === i ? { ...s, description: e.target.value } : s);
                              update({ externalSystems: updated });
                            }}
                            size="small" fullWidth sx={DF}
                            inputProps={{ style: { fontSize: '0.78rem' } }}
                          />
                        </Box>
                        <IconButton size="small"
                          onClick={() => update({ externalSystems: data.externalSystems.filter((_, j) => j !== i) })}
                          sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' }, alignSelf: 'flex-start' }}>
                          <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small" startIcon={<AddIcon />}
                      onClick={() => update({ externalSystems: [...data.externalSystems, { id: uid(), name: '', url: '', description: '' }] })}
                      sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Lägg till system
                    </Button>
                  </>
                )}
              </Section>

              {/* ── Funktioner ────────────────────────────────────────────── */}
              <Section>
                <SectionLabel>Funktioner</SectionLabel>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onFeatureDragEnd}>
                  <SortableContext items={data.features.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    {data.features.map(feat => (
                      <FeatureRow
                        key={feat.id}
                        feat={feat}
                        onChange={updated => {
                          const next = data.features.map(f => f.id === updated.id ? updated : f);
                          update({ features: next });
                        }}
                        onDelete={id => update({ features: data.features.filter(f => f.id !== id) })}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                <Button
                  size="small" startIcon={<AddIcon />}
                  onClick={() => update({ features: [...data.features, { id: uid(), name: '', description: '', order: data.features.length }] })}
                  sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.8rem', mt: 0.5 }}
                >
                  Lägg till funktion
                </Button>
              </Section>


              {/* ── Tech stack ────────────────────────────────────────────── */}
              <Section>
                <SectionLabel>Tech stack</SectionLabel>

                {(caseTechStack || []).length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', mb: 0.5, display: 'block' }}>
                      Ärvt från caset:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {caseTechStack.map(t => {
                        const tt = TECH_TYPES.find(x => x.key === t.type) || TECH_TYPES[7];
                        return (
                          <Chip key={t.id} label={`${t.name}${t.version ? ` ${t.version}` : ''}`} size="small"
                            sx={{ bgcolor: tt.bg + '55', color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', height: 18 }} />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {data.techStack.map(tech => (
                  <TechRow
                    key={tech.id}
                    tech={tech}
                    onChange={updated => update({ techStack: data.techStack.map(t => t.id === updated.id ? updated : t) })}
                    onDelete={id => update({ techStack: data.techStack.filter(t => t.id !== id) })}
                  />
                ))}
                <Button
                  size="small" startIcon={<AddIcon />}
                  onClick={() => update({ techStack: [...data.techStack, { id: uid(), name: '', type: 'package', version: '', url: '', note: '' }] })}
                  sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Lägg till tech
                </Button>
              </Section>

              {/* ── Referenser ────────────────────────────────────────────── */}
              <Section>
                <SectionLabel>Liknande lösningar / Inspirationskällor</SectionLabel>
                {data.references.map((ref, i) => (
                  <Box key={ref.id} sx={{
                    display: 'flex', gap: 1, mb: 0.75,
                    bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, p: 1,
                  }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl size="small" sx={{ ...DF, width: 130 }}>
                          <Select
                            value={ref.type}
                            onChange={e => {
                              const updated = data.references.map((r, j) => j === i ? { ...r, type: e.target.value } : r);
                              update({ references: updated });
                            }}
                            sx={{ '& .MuiSelect-select': { py: 0.6, fontSize: '0.75rem' } }}
                          >
                            {REF_TYPES.map(t => (
                              <MenuItem key={t.key} value={t.key} sx={{ fontSize: '0.8rem', color: t.color }}>
                                {t.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          placeholder="Namn *"
                          value={ref.name}
                          onChange={e => {
                            const updated = data.references.map((r, j) => j === i ? { ...r, name: e.target.value } : r);
                            update({ references: updated });
                          }}
                          size="small" sx={{ ...DF, flex: 1 }}
                          inputProps={{ style: { fontSize: '0.82rem' } }}
                        />
                        {ref.url && (
                          <IconButton size="small" href={ref.url} target="_blank" rel="noopener"
                            sx={{ color: MAPPN_ACCENT }}>
                            <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        )}
                      </Box>
                      <TextField
                        placeholder="URL"
                        value={ref.url}
                        onChange={e => {
                          const updated = data.references.map((r, j) => j === i ? { ...r, url: e.target.value } : r);
                          update({ references: updated });
                        }}
                        size="small" fullWidth sx={DF}
                        inputProps={{ style: { fontSize: '0.78rem' } }}
                      />
                      <TextField
                        placeholder="Kommentar"
                        value={ref.note}
                        onChange={e => {
                          const updated = data.references.map((r, j) => j === i ? { ...r, note: e.target.value } : r);
                          update({ references: updated });
                        }}
                        size="small" fullWidth sx={DF}
                        inputProps={{ style: { fontSize: '0.78rem' } }}
                      />
                    </Box>
                    <IconButton size="small"
                      onClick={() => update({ references: data.references.filter((_, j) => j !== i) })}
                      sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' }, alignSelf: 'flex-start' }}>
                      <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small" startIcon={<AddIcon />}
                  onClick={() => update({ references: [...data.references, { id: uid(), name: '', url: '', type: 'inspiration', note: '' }] })}
                  sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Lägg till referens
                </Button>
              </Section>

              {/* ── Anteckningar ──────────────────────────────────────────── */}
              <Section>
                <SectionLabel>Anteckningar</SectionLabel>
                <TextField
                  placeholder="Fri text…"
                  value={data.notes}
                  onChange={e => update({ notes: e.target.value })}
                  fullWidth multiline rows={3} size="small" sx={DF}
                />
              </Section>

              {/* ── Kommentarer ───────────────────────────────────────────── */}
              {!isNew && (
                <CommentThread caseId={caseId} componentId={componentId} />
              )}

              <Box sx={{ height: 24 }} />
            </Box>

            {/* Footer */}
            <Box sx={{
              px: 2.5, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.07)',
              display: 'flex', gap: 1, flexShrink: 0,
            }}>
              {isNew ? (
                <>
                  <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none' }}>
                    Avbryt
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!data.title.trim() || saving}
                    onClick={async () => { const ok = await doSave(data); if (ok) onClose?.(); }}
                    sx={{ bgcolor: MAPPN_ACCENT, '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none', ml: 'auto' }}
                  >
                    {saving ? 'Sparar…' : 'Skapa komponent'}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', alignSelf: 'center', flex: 1 }}>
                    {saving ? 'Sparar…' : 'Auto-sparas'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={saving}
                    onClick={() => { clearTimeout(saveTimer.current); doSave(comp, false); }}
                    sx={{
                      color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.15)',
                      textTransform: 'none', fontSize: '0.78rem',
                      '&:hover': { borderColor: MAPPN_ACCENT, color: MAPPN_ACCENT },
                    }}
                  >
                    Spara
                  </Button>
                </>
              )}
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Thin wrapper for section spacing
function Section({ children }) {
  return (
    <Box sx={{
      bgcolor: 'rgba(255,255,255,0.025)', borderRadius: 1.5,
      border: '1px solid rgba(255,255,255,0.05)', p: 2,
    }}>
      {children}
    </Box>
  );
}

